import { z } from 'zod';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { ApiError } from './api';

/**
 * Cliente HTTP + hooks para polarización 80/20 y PMC por disciplina (M2.32.B/D).
 *
 * Endpoints consumidos:
 *   - GET /api/analytics/polarization?weeks=N (default 8, max 26)
 *   - GET /api/load/by-discipline?days=N (default 180, max 730)
 *
 * Patrón: schemas Zod (source-of-truth del shape), `getJson` helper, hooks
 * de TanStack Query con keys agrupadas. Misma convención que `lib/analytics.ts`
 * y `lib/dashboard.ts`.
 *
 * Decisión de fichero independiente (no extender `lib/analytics.ts`): los
 * dos endpoints viven en routes diferentes (`/api/analytics` y `/api/load`),
 * y mantener la separación facilita encontrar el código en futuras
 * iteraciones (M2.32.E tooltips, M3+ ingestión de streams para zonas).
 */

const API_BASE_URL: string = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');

// ---------------------------------------------------------------------------
// Schemas: polarization
// ---------------------------------------------------------------------------

export const PolarizationWeekSchema = z.object({
  weekStartISO: z.string(),
  totalMinutes: z.number().nonnegative(),
  lowMinutes: z.number().nonnegative(),
  tempoMinutes: z.number().nonnegative(),
  hardMinutes: z.number().nonnegative(),
  polarizedRatio: z.number().nullable(),
});
export type PolarizationWeek = z.infer<typeof PolarizationWeekSchema>;

export const PolarizationResponseSchema = z.object({
  weeks: z.array(PolarizationWeekSchema),
});
export type PolarizationResponse = z.infer<typeof PolarizationResponseSchema>;

// ---------------------------------------------------------------------------
// Schemas: PMC por disciplina
// ---------------------------------------------------------------------------

export const DisciplinePmcBucketSchema = z.object({
  swim: z.number(),
  bike: z.number(),
  run: z.number(),
  strength: z.number(),
  brick: z.number(),
  other: z.number(),
});
export type DisciplinePmcBucket = z.infer<typeof DisciplinePmcBucketSchema>;

export const DisciplinePmcDaySchema = z.object({
  dateISO: z.string(),
  ctl: z.number(),
  atl: z.number(),
  tsb: z.number(),
  ctlByDiscipline: DisciplinePmcBucketSchema,
  atlByDiscipline: DisciplinePmcBucketSchema,
});
export type DisciplinePmcDay = z.infer<typeof DisciplinePmcDaySchema>;

export const LoadByDisciplineResponseSchema = z.object({
  days: z.array(DisciplinePmcDaySchema),
  fromISO: z.string(),
  toISO: z.string(),
});
export type LoadByDisciplineResponse = z.infer<typeof LoadByDisciplineResponseSchema>;

// ---------------------------------------------------------------------------
// HTTP helper
// ---------------------------------------------------------------------------

async function readJsonSafe(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

async function getJson<T>(path: string, schema: z.ZodType<T>): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
  } catch (err) {
    throw new ApiError('Error de red al contactar con el backend', 0, err);
  }
  if (!response.ok) {
    throw new ApiError(`Respuesta no OK del backend: ${response.status}`, response.status);
  }
  const json = await readJsonSafe(response);
  const parsed = schema.safeParse(json);
  if (!parsed.success) {
    throw new ApiError(`Respuesta ${path} no cumple esquema`, response.status, parsed.error);
  }
  return parsed.data;
}

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

export function getWeeklyPolarization(weeks: number): Promise<PolarizationResponse> {
  return getJson(
    `/api/analytics/polarization?weeks=${encodeURIComponent(String(weeks))}`,
    PolarizationResponseSchema,
  );
}

export function getLoadByDiscipline(days: number): Promise<LoadByDisciplineResponse> {
  return getJson(
    `/api/load/by-discipline?days=${encodeURIComponent(String(days))}`,
    LoadByDisciplineResponseSchema,
  );
}

// ---------------------------------------------------------------------------
// Query keys + hooks
// ---------------------------------------------------------------------------

export const POLARIZATION_QUERY_KEY = ['analytics', 'polarization'] as const;
export const LOAD_BY_DISCIPLINE_QUERY_KEY = ['load', 'by-discipline'] as const;

export function useWeeklyPolarization(
  weeks: number,
): UseQueryResult<PolarizationResponse, Error> {
  return useQuery<PolarizationResponse, Error>({
    queryKey: [...POLARIZATION_QUERY_KEY, weeks],
    queryFn: () => getWeeklyPolarization(weeks),
    staleTime: 60_000,
    retry: 1,
  });
}

export function useLoadByDiscipline(
  days: number,
): UseQueryResult<LoadByDisciplineResponse, Error> {
  return useQuery<LoadByDisciplineResponse, Error>({
    queryKey: [...LOAD_BY_DISCIPLINE_QUERY_KEY, days],
    queryFn: () => getLoadByDiscipline(days),
    staleTime: 60_000,
    retry: 1,
  });
}
